import classNames from 'classnames';
import isEmpty from 'lodash/isEmpty';
import isNumber from 'lodash/isNumber';
import { memo } from 'react';
import { ClanLeagueSignInline, DivTooltip, HiddenStatisticsIcon, Tooltip, TooltipBody } from '@wg/wows-react-uikit';

import settings from '~/settings';
import { BATTLE_TYPES } from '~/constants';
import { DEFAULT_ERROR, percent, thousands } from '~/helpers/formatting';
import { isClanBattleType } from '~/helpers/ladder';
import { getLocalizationSafe, t } from '~/helpers/localization';
import { getSeasonLeagues, lastSeason } from '~/helpers/seasons';

import { ClanLeagueSign } from '~/UIKit';

import styles from './Ratings.scss';

import type { ITeamStatistics } from '~/types/declaration';

interface RatingsProps {
  clanId?: number;
  membersError: boolean;
  currentSeason: number;
  statistics: ITeamStatistics;
  isHiddenStatistics: boolean;
}

const Ratings = ({ membersError, currentSeason, statistics, isHiddenStatistics }: RatingsProps) => {
  const membersErrorTooltip = t('Не удалось получить информацию об игроках клана:tooltip');

  const renderWinsCounts = () => {
    if (statistics.currentBattleType !== BATTLE_TYPES.BRAWL_CVC) {
      return null;
    }

    const value = isHiddenStatistics ? (
      <HiddenStatisticsIcon isLarger isClan />
    ) : (
      <span>{thousands(statistics.ladderWinsCount ?? 0)}</span>
    );

    const tooltipBody = !isHiddenStatistics ? (
      <Tooltip>
        <TooltipBody>{membersError ? membersErrorTooltip : t('Количество побед')}</TooltipBody>
      </Tooltip>
    ) : null;

    return (
      <dl className={styles.item}>
        <DivTooltip tooltipBody={tooltipBody}>
          <dd className={styles.value}>{value}</dd>
          <dt>{t('Количество побед')}</dt>
        </DivTooltip>
      </dl>
    );
  };

  const renderAverageBattlesCounts = () => {
    if (statistics.currentBattleType === BATTLE_TYPES.BRAWL_CVC) {
      return null;
    }

    const value = isHiddenStatistics ? (
      <HiddenStatisticsIcon isLarger isClan />
    ) : (
      <span>{thousands(statistics.averageBattlesCounts || 0)}</span>
    );

    const tooltipBody = !isHiddenStatistics ? (
      <Tooltip>
        <TooltipBody>
          {membersError
            ? membersErrorTooltip
            : t('Среднее количество боёв на игрока клана в выбранном типе боев за всё время:tooltip')}
        </TooltipBody>
      </Tooltip>
    ) : null;

    return !isClanBattleType(statistics.currentBattleType) ? (
      <dl className={styles.item}>
        <dl className={styles.item}>
          <DivTooltip tooltipBody={tooltipBody}>
            <dd className={styles.value}>{value}</dd>
            <dt>{t('Количество боёв')}</dt>
          </DivTooltip>
        </dl>
      </dl>
    ) : null;
  };

  const renderAverageWins = () => {
    const value = isHiddenStatistics ? (
      <HiddenStatisticsIcon isLarger isClan />
    ) : (
      <span>{percent(statistics.averageWinsPercentage || 0)}</span>
    );

    const tooltipBody = !isHiddenStatistics ? (
      <Tooltip>
        <TooltipBody>
          {membersError
            ? membersErrorTooltip
            : t('Средний процент побед на игрока клана в выбранном типе боев за всё время:tooltip')}
        </TooltipBody>
      </Tooltip>
    ) : null;

    return !isClanBattleType(statistics.currentBattleType) ? (
      <dl className={styles.item}>
        <DivTooltip tooltipBody={tooltipBody}>
          <dd className={styles.value}>{value}</dd>
          <dt>{t('Победы')}</dt>
        </DivTooltip>
      </dl>
    ) : null;
  };

  const renderClanBattlesCount = () => {
    if (statistics.currentBattleType === BATTLE_TYPES.BRAWL_CVC) {
      return null;
    }

    const tooltipBody = !isHiddenStatistics ? (
      <Tooltip>
        <TooltipBody>{t('Общее количество боев клана за сезон:tooltip')}</TooltipBody>
      </Tooltip>
    ) : null;

    return isClanBattleType(statistics.currentBattleType) ? (
      <dl className={styles.item}>
        <DivTooltip tooltipBody={tooltipBody}>
          <dd className={styles.value}>{thousands(statistics.ladderBattlesCount || 0)}</dd>
          <dt>{t('Количество боёв')}</dt>
        </DivTooltip>
      </dl>
    ) : null;
  };

  const renderClanWins = () => {
    const tooltipBody = !isHiddenStatistics ? (
      <Tooltip>
        <TooltipBody>{t('Процент побед клана за сезон:tooltip')}</TooltipBody>
      </Tooltip>
    ) : null;

    return isClanBattleType(statistics.currentBattleType) ? (
      <dl className={styles.item}>
        <DivTooltip tooltipBody={tooltipBody}>
          <dd className={styles.value}>{percent(statistics.ladderWinsPercent || 0)}</dd>
          <dt>{t('Победы')}</dt>
        </DivTooltip>
      </dl>
    ) : null;
  };

  const renderMembersRating = () => {
    const totalMembers = isNumber(statistics.totalMembers) ? statistics.totalMembers : DEFAULT_ERROR;
    const maxMembersCount = isNumber(statistics.maxMembersCount) ? statistics.maxMembersCount : DEFAULT_ERROR;

    return !statistics.withLeagueSign ? (
      <dl className={styles.item}>
        <DivTooltip
          tooltipBody={
            <Tooltip>
              <TooltipBody>
                {membersError && !isNumber(statistics.totalMembers)
                  ? membersErrorTooltip
                  : t('Количество игроков в клане / Максимальная численность клана:tooltip')}
              </TooltipBody>
            </Tooltip>
          }
        >
          <dd>
            <span className={styles.value}>{totalMembers}</span>
            <span className={styles.total}>&nbsp;/&nbsp;</span>
            <span className={styles.total}>{maxMembersCount}</span>
          </dd>
          <dt>{t('Игроков в клане')}</dt>
        </DivTooltip>
      </dl>
    ) : null;
  };

  const renderLeagueSign = () => {
    const defaultMaxDivisionRating = 100;

    return !isEmpty(lastSeason) && statistics.withLeagueSign ? (
      <dl className={styles.medalWrapper}>
        <ClanLeagueSign
          clanDivisionRating={statistics.divisionRating || 0}
          clanMaxDivisionRating={statistics.maxDivisionRating || defaultMaxDivisionRating}
          clanDivision={statistics.division}
          clanLeague={statistics.league}
          clanStage={statistics.stage}
          isFullSize={true}
        />
      </dl>
    ) : null;
  };

  const renderAverageExpPerBattle = () => {
    const value = isHiddenStatistics ? (
      <HiddenStatisticsIcon isLarger isClan />
    ) : (
      <span>{thousands(statistics.averageExperiencePerBattle || 0)}</span>
    );

    const tooltipBody = !isHiddenStatistics ? (
      <Tooltip>
        <TooltipBody>
          {membersError
            ? membersErrorTooltip
            : t('Средний опыт за бой на игрока клана в выбранном типе боев за всё время:tooltip')}
        </TooltipBody>
      </Tooltip>
    ) : null;

    return !isClanBattleType(statistics.currentBattleType) ? (
      <dl className={styles.item}>
        <DivTooltip tooltipBody={tooltipBody}>
          <dd className={styles.value}>{value}</dd>
          <dt>{t('Опыт за бой')}</dt>
        </DivTooltip>
      </dl>
    ) : null;
  };

  const renderAverageDamagePerBattle = () => {
    const value = isHiddenStatistics ? (
      <HiddenStatisticsIcon isLarger isClan />
    ) : (
      <span>{thousands(statistics.averageDamagePerBattle || 0)}</span>
    );

    const tooltipBody = !isHiddenStatistics ? (
      <Tooltip>
        <TooltipBody>
          {membersError
            ? membersErrorTooltip
            : t('Средний урон за бой на игрока клана в выбранном типе боев за всё время:tooltip')}
        </TooltipBody>
      </Tooltip>
    ) : null;

    return !isClanBattleType(statistics.currentBattleType) ? (
      <dl className={styles.item}>
        <DivTooltip tooltipBody={tooltipBody}>
          <dd className={styles.value}>{value}</dd>
          <dt>{t('Урон за бой')}</dt>
        </DivTooltip>
      </dl>
    ) : null;
  };

  const renderInlineSign = () => {
    if (statistics.currentBattleType !== BATTLE_TYPES.REGULAR_CVC) {
      return null;
    }

    if (!isClanBattleType(statistics.currentBattleType) || currentSeason === null) {
      return null;
    }

    if (isHiddenStatistics || isEmpty(settings.ladder.seasons)) {
      return null;
    }

    const leaguesInfo = getSeasonLeagues(currentSeason);

    if (!statistics.ladderMaxLeague) {
      return null;
    }

    const currentLeague = leaguesInfo[statistics.ladderMaxLeague];

    if (!currentLeague || !statistics.ladderMaxDivision) {
      return null;
    }

    const leagueDivisionsLength = currentLeague.divisions.length;

    const leagueDivisionInfoTitle =
      currentLeague.divisions[leagueDivisionsLength - statistics.ladderMaxDivision]?.title ?? '';

    const leagueColor = currentLeague.color;
    const leagueTitle = getLocalizationSafe(currentLeague, 'mark');

    const tooltipBody = (
      <Tooltip>
        <TooltipBody>
          <div>
            {t('Максимальное значение рейтинга клана за сезон:tooltip')}
            <br />
            {t('Лига:')} {leagueTitle},&nbsp;
            {t('Дивизион:')} {leagueDivisionInfoTitle}
            <br />
            {t('Рейтинг в дивизионе:')} {statistics.ladderMaxDivisionRating}
          </div>
        </TooltipBody>
      </Tooltip>
    );

    return (
      <dl className={styles.item}>
        <DivTooltip tooltipBody={tooltipBody}>
          <dd className={styles.value}>
            <span className={styles.leagueInlineSign}>
              <ClanLeagueSignInline color={leagueColor} title={leagueDivisionInfoTitle} />
            </span>
            <span>{statistics.ladderMaxDivisionRating}</span>
          </dd>
          <dt>{t('Рекорд сезона')}</dt>
        </DivTooltip>
      </dl>
    );
  };

  const renderWinStreak = () => {
    const tooltipBody = !isHiddenStatistics ? (
      <Tooltip>
        <TooltipBody>{t('Максимальная длина череды побед клана за сезон:tooltip')}</TooltipBody>
      </Tooltip>
    ) : null;

    return isClanBattleType(statistics.currentBattleType) ? (
      <dl className={styles.item}>
        <DivTooltip tooltipBody={tooltipBody}>
          <dd className={styles.value}>{thousands(statistics.ladderWinStreak || 0)}</dd>
          <dt>{t('Победная серия')}</dt>
        </DivTooltip>
      </dl>
    ) : null;
  };

  return (
    <div
      className={classNames(styles.ratings, {
        [styles.center]: statistics.withLeagueSign,
      })}
    >
      {renderLeagueSign()}
      {renderAverageBattlesCounts()}
      {renderWinsCounts()}
      {renderAverageWins()}
      {renderClanBattlesCount()}
      {renderClanWins()}
      {renderMembersRating()}
      {renderAverageExpPerBattle()}
      {renderAverageDamagePerBattle()}
      {renderInlineSign()}
      {renderWinStreak()}
    </div>
  );
};

export default memo(Ratings);
